import React, { useEffect, useState } from 'react';
import { getActivityLog, getActivityLogFilters, getCountries, getStaff, getDocumentDownload } from '../api/admin';
import { useCountry } from '../context/CountryContext';

const STATUS_LABELS = {
  pending_acceptance: 'Awaiting acceptance', accepted: 'Accepted', declined: 'Declined', withdrawn: 'Withdrawn',
  expired: 'Expired', reassignment_requested: 'Return requested', access_revoked: 'Access revoked', pending: 'Pending', verified: 'Verified',
  submitted_for_acceptance: 'Submitted for acceptance', completed: 'Completed',
};

const DOCUMENT_LABELS = { order_document: 'Order Document', delivery_note: 'Delivery Note' };

async function downloadDocument(documentId) {
  try {
    const res = await getDocumentDownload(documentId);
    window.open(res.data.url, '_blank');
  } catch (err) {
    alert(err.response?.data?.error || 'Document is not ready yet.');
  }
}

function formatWhen(iso) {
  return new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });
}

function renderChangeValue(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    if ('status' in v) return STATUS_LABELS[v.status] || v.status;
    if ('country' in v) return v.country || 'Not assigned';
    const keys = Object.keys(v);
    if (!keys.length) return null;
    // Long nested values (like a permission set) are summarised, not dumped.
    return keys.map((k) => {
      const val = v[k];
      if (val && typeof val === 'object') {
        const count = Object.values(val).filter((x) => x === true).length;
        return k + ': ' + (Object.keys(val).length ? count + ' enabled' : 'none');
      }
      return k.replace(/_/g, ' ') + ': ' + (typeof val === 'boolean' ? (val ? 'yes' : 'no') : (STATUS_LABELS[val] || String(val)));
    }).join(', ');
  }
  return String(v);
}

// Activity Log & Reference Numbers brief (05.10.2026) — readable, filterable
// owner-facing log. Merges the existing append-only sources (admin_logs,
// audit_events, case_assignment_events) — nothing about how they're stored
// changes; this only translates and groups them for display. Security
// review (08.10.2026): the server no longer sends raw/technical data in
// this response at all -- not just hidden behind a button -- so there is
// nothing left here to expand.
const ActivityLog = () => {
  // Joint Architecture: the global country selector drives this list too --
  // no separate, independent country dropdown here anymore. An explicit
  // override is still offered ("All countries" view for owners), kept in
  // sync with the header instead of living apart from it.
  const { country: globalCountry } = useCountry();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from: '', to: '', admin_id: '', result: '', area: '', object_type: '', q: '' });
  const [countries, setCountries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [areas, setAreas] = useState([]);
  const [objectTypes, setObjectTypes] = useState([]);

  const load = () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (globalCountry && globalCountry !== 'ALL' && globalCountry !== 'NONE') params.country = globalCountry;
    getActivityLog(params).then((res) => setEntries(res.data?.entries || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filters, globalCountry]); // eslint-disable-line
  useEffect(() => {
    getCountries().then((res) => setCountries(res.data?.countries || []));
    getStaff({ limit: 200 }).then((res) => setStaff(res.data?.staff || []));
    getActivityLogFilters().then((res) => { setAreas(res.data?.areas || []); setObjectTypes(res.data?.object_types || []); });
  }, []);

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Activity Log</h1>
      <p style={s.hint}>Who changed what, when, on which object, in which country, and with what result.</p>

      <div style={s.filters}>
        <input style={s.input} type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} title="From date" />
        <input style={s.input} type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} title="To date" />
        <span style={s.globalCountryTag}>Showing: {globalCountry === 'ALL' ? 'All countries' : globalCountry === 'NONE' ? 'No active country' : (countries.find((c) => c.country_code === globalCountry)?.country_name || globalCountry)} <span style={s.titleSmall}>(set in the header)</span></span>
        <select style={s.input} value={filters.admin_id} onChange={(e) => setFilters((f) => ({ ...f, admin_id: e.target.value }))}>
          <option value="">All admins</option>
          {staff.map((st) => <option key={st.id} value={st.id}>{st.full_name || st.email}</option>)}
        </select>
        <select style={s.input} value={filters.result} onChange={(e) => setFilters((f) => ({ ...f, result: e.target.value }))}>
          <option value="">All results</option>
          <option value="success">Successful</option>
          <option value="denied">Denied</option>
          <option value="blocked">Blocked</option>
          <option value="failed">Failed</option>
        </select>
        <select style={s.input} value={filters.area} onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))}>
          <option value="">All areas</option>
          {areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select style={s.input} value={filters.object_type} onChange={(e) => setFilters((f) => ({ ...f, object_type: e.target.value }))}>
          <option value="">All object types</option>
          {objectTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <input style={s.input} placeholder="Search Customer #, DC-2048, name…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
      </div>

      {loading ? <p>Loading…</p> : (
        <div>
          {entries.map((e) => (
            <div key={e.id} style={s.card}>
              <div style={s.cardTop}>
                <span style={s.timestamp}>{formatWhen(e.timestamp)}</span>
                <span style={s.actor}>{e.actor?.full_name || e.actor?.email || 'System'}</span>
                <span style={{ ...s.resultBadge, ...(e.result === 'success' ? s.resultOk : s.resultBad) }}>{e.result === 'success' ? 'Successful' : e.result}</span>
              </div>
              <div style={s.area}>{e.area}</div>
              <div style={s.actionLabel}>
                {e.action_label}
                {e.grouped_objects?.length > 1 ? ` (${e.grouped_objects.length})` : ''}
              </div>
              {e.object_ref && <div style={s.row}><span style={s.label}>Affected:</span> {e.grouped_objects?.length > 1 ? e.grouped_objects.join(', ') : e.object_ref}</div>}
              {e.country_code && <div style={s.row}><span style={s.label}>Country:</span> {e.country_code}</div>}
              {e.assignment_details && (
                <>
                  <div style={s.row}><span style={s.label}>Assigned to:</span> {e.assignment_details.assigned_to}</div>
                  {e.assignment_details.priority && <div style={s.row}><span style={s.label}>Priority:</span> {e.assignment_details.priority}</div>}
                  {e.assignment_details.acceptance_due_at && <div style={s.row}><span style={s.label}>Acceptance deadline:</span> {new Date(e.assignment_details.acceptance_due_at).toLocaleString()}</div>}
                </>
              )}
              {(renderChangeValue(e.previous_value) || renderChangeValue(e.new_value)) && (
                <div style={s.row}><span style={s.label}>Change:</span> {renderChangeValue(e.previous_value) || '—'} → {renderChangeValue(e.new_value) || '—'}</div>
              )}
              {e.reason && <div style={s.row}><span style={s.label}>Reason:</span> {e.reason}</div>}
              {e.documents?.length > 0 && (
                <div style={s.docRow}>
                  {e.documents.map((d) => (
                    <button key={d.id} style={s.docBtn} onClick={() => downloadDocument(d.id)}>Download {DOCUMENT_LABELS[d.type]} · PDF</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {entries.length === 0 && <p style={s.empty}>No activity matches these filters.</p>}
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1000 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 14, maxWidth: 700 },
  filters: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 },
  empty: { color: '#9ca3af', fontSize: 13 },
  card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 10, minWidth: 0, overflowWrap: 'anywhere' },
  cardTop: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' },
  timestamp: { fontSize: 12, color: '#6b7280', fontWeight: 700 },
  actor: { fontSize: 12.5, color: '#111827', fontWeight: 700 },
  resultBadge: { marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: '2px 8px' },
  resultOk: { color: '#166534', background: '#dcfce7' },
  resultBad: { color: '#991b1b', background: '#fee2e2' },
  area: { fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 },
  actionLabel: { fontSize: 14.5, fontWeight: 700, color: '#111827', marginBottom: 6 },
  row: { fontSize: 12.5, color: '#374151', marginBottom: 2, overflowWrap: 'anywhere', wordBreak: 'break-word' },
  label: { color: '#9ca3af', marginRight: 4 },
  detailsBtn: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 11.5, padding: 0, marginTop: 6 },
  technical: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: 8 },
  globalCountryTag: { fontSize: 12.5, fontWeight: 700, color: '#374151', background: '#f3f4f6', borderRadius: 8, padding: '8px 10px' },
  docRow: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  docBtn: { padding: '6px 12px', borderRadius: 6, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' },
  titleSmall: { color: '#9ca3af', fontSize: 11, fontWeight: 400 },
};

export default ActivityLog;
